import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './LookupModal.css';

/**
 * LookupModal - 통합된 Lookup 선택 모달
 * 검색창과 결과 목록을 하나의 모달에 통합
 */
const LookupModal = ({ 
  isOpen = false,
  onClose,
  onSelect,
  
  // 검색 설정
  searchFunction,
  
  // 표시 설정  
  title = "항목 선택",
  displayFields = [],
  placeholder = "검색어를 입력하세요...",
  
  // 초기 로딩
  initialResults = [],
  isLoading = false
}) => {
  // 상태 관리
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(initialResults);
  const [isSearching, setIsSearching] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // 검색 입력 ref
  const searchInputRef = useRef(null);
  
  // 디바운스 타이머
  const searchTimerRef = useRef(null);
  
  // onClose 함수를 안정적으로 유지
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // 모달 열기/닫기 애니메이션
  useEffect(() => {
    // ESC 키 핸들러
    const handleKeyDownInternal = (e) => {
      if (e.key === 'Escape') {
        onCloseRef.current?.();
      }
    };

    if (isOpen) {
      setIsVisible(true);
      // initialResults는 초기 설정 시에만 사용
      setSearchResults(initialResults || []);
      document.addEventListener('keydown', handleKeyDownInternal);
      document.body.style.overflow = 'hidden';
      
      setTimeout(() => {
        setIsAnimating(true);
        // 검색창에 포커스
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 10);
      
      // 클린업 함수 리턴
      return () => {
        document.removeEventListener('keydown', handleKeyDownInternal);
        document.body.style.overflow = 'unset';
      };
    } else {
      setIsAnimating(false);
      document.body.style.overflow = 'unset';
      
      setTimeout(() => {
        setIsVisible(false);
        setSearchQuery('');
        setSearchResults([]);
        setIsSearching(false);
      }, 300);
    }
  }, [isOpen]); // isOpen만 의존성으로 유지

  // ESC 키로 닫기
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose?.();
    }
  }, [onClose]);

  // 오버레이 클릭으로 닫기
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  }, [onClose]);

  // 검색어 변경 처리 (입력만 처리, 자동 검색 없음)
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // 기존 타이머 클리어 (자동 검색 방지)
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
  }, []);

  // 실제 검색 실행 함수
  const performSearch = useCallback(async (query) => {
    if (!searchFunction) return;
    
    console.log('검색 실행:', query);
    setIsSearching(true);
    
    try {
      const results = await searchFunction(query);
      console.log('검색 결과:', results);
      setSearchResults(results || []);
    } catch (error) {
      console.error('검색 오류:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchFunction]);

  // 엔터 키 처리 (엔터를 쳐야만 검색)
  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      performSearch(searchQuery);
    }
  }, [performSearch, searchQuery]);

  // 항목 선택 처리
  const handleSelectItem = useCallback((item) => {
    onSelect?.(item);
    onClose?.();
  }, [onSelect, onClose]);

  // 필드값 포맷팅
  const formatFieldValue = useCallback((value, fieldName) => {
    if (value === null || value === undefined) {
      return '-';
    }
    
    // 날짜 필드 포맷팅
    if (fieldName.includes('date') || fieldName.includes('time')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('ko-KR');
        }
      } catch (e) {
        // 날짜 파싱 실패시 원본값 반환
      }
    }
    
    // 문자열이 너무 길면 줄임
    if (typeof value === 'string' && value.length > 30) {
      return value.substring(0, 30) + '...';
    }
    
    return String(value);
  }, []);

  // 표시할 필드 정보 가져오기
  const getDisplayFields = useCallback(() => {
    if (displayFields && displayFields.length > 0) {
      return displayFields;
    }
    
    // displayFields가 없으면 첫 번째 결과에서 자동 추출
    if (searchResults && searchResults.length > 0) {
      const firstResult = searchResults[0];
      return Object.keys(firstResult)
        .filter(key => !key.includes('_id') && !key.includes('password'))
        .slice(0, 4)
        .map(key => ({ field: key, label: key }));
    }
    
    return [];
  }, [displayFields, searchResults]);

  const fieldsToShow = getDisplayFields();

  if (!isVisible) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={`lookup-modal-overlay ${isAnimating ? 'open' : ''}`}>
      <div className="lookup-modal-backdrop" onClick={handleOverlayClick}></div>
      
      <div className={`lookup-modal ${isAnimating ? 'open' : ''}`}>
        {/* 헤더 */}
        <div className="lookup-modal-header">
          <h3 className="lookup-modal-title">{title}</h3>
          <button 
            className="lookup-modal-close" 
            onClick={onClose}
            aria-label="모달 닫기"
          >
            ✕
          </button>
        </div>

        {/* 검색 영역 */}
        <div className="lookup-modal-search">
          <div className="lookup-search-input-wrapper">
            <input
              ref={searchInputRef}
              type="text"
              className="lookup-search-input"
              placeholder={placeholder + " (엔터를 눌러 검색)"}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
            />
            <button
              type="button"
              className="lookup-search-button"
              onClick={() => performSearch(searchQuery)}
              disabled={isSearching}
              title="검색"
            >
              {isSearching ? (
                <div className="spinner-small"></div>
              ) : (
                <span>🔍</span>
              )}
            </button>
          </div>
        </div>

        {/* 결과 영역 */}
        <div className="lookup-modal-content">
          {(isLoading || isSearching) ? (
            <div className="lookup-loading">
              <div className="spinner"></div>
              <p>검색 중...</p>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="lookup-empty">
              {searchQuery ? (
                <p>🔍 '{searchQuery}'에 대한 검색 결과가 없습니다.</p>
              ) : (
                <div>
                  <p>🔍 검색어를 입력하여 항목을 찾아보세요</p>
                  <p className="lookup-empty-hint">예: ID, 코드, 이름 등</p>
                </div>
              )}
            </div>
          ) : (
            <div className="lookup-results-container">
              <div className="lookup-results-header">
                <span>검색 결과 ({searchResults.length}개)</span>
              </div>
              
              <div className="lookup-results-table-container">
                <table className="lookup-results-table">
                  <thead>
                    <tr>
                      {fieldsToShow.map(field => (
                        <th key={field.field}>{field.label}</th>
                      ))}
                      <th>선택</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((result, index) => (
                      <tr 
                        key={index} 
                        className="lookup-result-row"
                        onClick={() => handleSelectItem(result)}
                      >
                        {fieldsToShow.map(field => (
                          <td key={field.field} title={result[field.field]}>
                            {formatFieldValue(result[field.field], field.field)}
                          </td>
                        ))}
                        <td>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectItem(result);
                            }}
                            className="lookup-select-btn"
                            title="선택"
                          >
                            선택
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="lookup-modal-footer">
          <button 
            onClick={onClose}
            className="lookup-modal-cancel-btn"
          >
            취소
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LookupModal; 